import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { copyToClipboard } from "../lib/clipboard";
import { BrutalButton } from "./Brutal";
import { DownloadSimple, Printer, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const GOLD = "#c9a24a";
const GOLD_DARK = "#8a6a1c";

/** Compose the printable card as a PNG data-URL from the hidden QR canvas.
 * v412.4 — `url` renders the tappable store link's text on the card itself,
 * below the QR and above the instruction footer.
 * v414.43 — optional `tagline` (e.g. "Curious about peptides? Just ask.")
 * printed in gold between the code box and the QR. */
export function composeCardPng(qrCanvas, { name, code, heading, instruction, url, tagline }) {
  const W = 900, H = 1180;
  // v412.7 — export at 2x so the shared/saved PNG is large and crisp
  // (share-sheet previews shrink it; the delivered file stays sharp).
  const SCALE = 2;
  const c = document.createElement("canvas");
  c.width = W * SCALE; c.height = H * SCALE;
  const ctx = c.getContext("2d");
  ctx.scale(SCALE, SCALE);
  ctx.imageSmoothingEnabled = false; // keep QR modules razor sharp

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 10;
  ctx.strokeRect(20, 20, W - 40, H - 40);
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.textAlign = "center";
  ctx.fillStyle = GOLD_DARK;
  ctx.font = "600 30px ui-monospace, monospace";
  ctx.fillText(heading.toUpperCase(), W / 2, 130);

  ctx.fillStyle = "#09090b";
  ctx.font = "700 52px ui-sans-serif, system-ui";
  ctx.fillText(name, W / 2, 220);

  // Gold code box
  ctx.fillStyle = "#fbf6e7";
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 4;
  const bw = 520, bh = 130, bx = (W - bw) / 2, by = 270;
  ctx.fillRect(bx, by, bw, bh);
  ctx.strokeRect(bx, by, bw, bh);
  ctx.fillStyle = GOLD_DARK;
  ctx.font = "700 84px ui-monospace, monospace";
  ctx.fillText(code, W / 2, by + 95);

  if (qrCanvas) ctx.drawImage(qrCanvas, (W - 480) / 2, 470, 480, 480);

  // v414.43 — gold tagline between the code box and the QR (auto-shrinks to fit)
  const tg = (tagline || "").trim();
  if (tg) {
    ctx.fillStyle = GOLD_DARK;
    let fs = 36;
    ctx.font = `italic 600 ${fs}px ui-sans-serif, system-ui`;
    while (ctx.measureText(tg).width > W - 160 && fs > 22) {
      fs -= 2;
      ctx.font = `italic 600 ${fs}px ui-sans-serif, system-ui`;
    }
    ctx.fillText(tg, W / 2, 448);
  }

  // v412.4 — the direct store URL, printed between the QR and the footer.
  let iy = 1030, lh = 42;
  if (url) {
    ctx.fillStyle = GOLD_DARK;
    ctx.font = "600 32px ui-monospace, monospace";
    ctx.fillText(url.replace(/^https?:\/\//, ""), W / 2, 996);
    iy = 1050; lh = 40;
  }

  // Wrapped instruction footer
  ctx.fillStyle = "#52525b";
  ctx.font = "400 30px ui-sans-serif, system-ui";
  const words = instruction.split(" ");
  let line = "", y = iy;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > W - 180) {
      ctx.fillText(line, W / 2, y);
      line = w; y += lh;
    } else line = test;
  }
  if (line) ctx.fillText(line, W / 2, y);
  return c.toDataURL("image/png");
}

/** v408.5 — save a composed PNG in an iOS-friendly way.
 * iPhone Safari silently ignores `a.download` on data URLs, so the native
 * share sheet ("Save Image") is used when available; desktop falls back to a
 * normal download. Returns "shared" | "downloaded" | "cancelled". */
export async function saveCardPng(png, filename, url) {
  try {
    const blob = await (await fetch(png)).blob();
    const file = new File([blob], filename, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      // v412.6 — zero friction for the recipient: the link rides along as
      // real text, so whoever receives the card can tap or copy it instead
      // of needing a second phone to scan the QR.
      const payload = { files: [file] };
      if (url) payload.text = `Click or copy this link to shop: ${url}`;
      await navigator.share(payload);
      return "shared";
    }
  } catch (e) {
    if (e?.name === "AbortError") return "cancelled";
    // fall through to download on any share failure
  }
  const a = document.createElement("a");
  a.href = png;
  a.download = filename;
  a.click();
  return "downloaded";
}

/** v408.5 — QR card preview dialog. Shows the composed card as a real <img>
 * so iPhone users can long-press -> "Save Image", plus a share/download
 * button that uses the native share sheet when available. */
export function QrSaveDialog({ png, filename, url, onClose }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const how = await saveCardPng(png, filename, url);
      if (how === "downloaded") toast.success(t("code_card.downloaded"));
      if (how === "shared") toast.success(t("code_card.share_saved"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-4 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid="qr-save-dialog"
      >
        <img
          src={png}
          alt="Store QR card"
          className="w-full rounded-lg border border-zinc-200"
          data-testid="qr-save-image"
        />
        {url && (
          <div className="flex items-stretch gap-2 mt-3">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex-1 min-w-0 flex items-center justify-center text-xs font-mono text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-lg px-3 py-2 hover:bg-[#f5ecd2] transition-colors truncate"
              title={url}
              data-testid="qr-save-store-link"
            >
              <span className="truncate">{url.replace(/^https?:\/\//, "")}</span>
            </a>
            <button
              type="button"
              onClick={() => copyToClipboard(url, t("code_card.link_copied"))}
              className="shrink-0 px-3 rounded-lg border border-[#c9a24a]/60 bg-[#fbf6e7] text-[#8a6a1c] hover:bg-[#f5ecd2] transition-colors"
              title={t("code_card.link_copied")}
              data-testid="qr-save-copy-link-btn"
            >
              <Copy size={14} weight="bold" />
            </button>
          </div>
        )}
        <p className="text-xs text-zinc-500 text-center mt-2" data-testid="qr-save-hint">
          {t("code_card.longpress_hint")}
        </p>
        <div className="flex gap-2 mt-3">
          <BrutalButton size="sm" className="flex-1" onClick={save} disabled={busy} data-testid="qr-save-btn">
            <DownloadSimple size={14} weight="bold" /> {t("code_card.save_image")}
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" className="flex-1" onClick={onClose} data-testid="qr-save-close">
            {t("common.close")}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}

export function CodeCardDialog({ distributor, onClose }) {
  const { t } = useTranslation();
  const qrWrapRef = useRef(null);
  const pngQrRef = useRef(null); // v412.7 — hidden hi-res QR for PNG export
  const [busy, setBusy] = useState(false);
  // v405.15 — two card types: consignee balance card (portal) + Store QR.
  const [variant, setVariant] = useState("store");
  // v414.43 — editable tagline on the store card (prefilled, clearable).
  const [tagline, setTagline] = useState(t("code_card.tagline_default"));
  const code = distributor.distributor_code;
  const portalUrl = `${window.location.origin}/portal/${code}`;
  // v405.15b — stores live at the ROOT of the domain (reupclub.com/ABRV1)
  const storeUrl = `${window.location.origin}/${code}`;
  const qrUrl = variant === "store" ? storeUrl : portalUrl;
  const heading = variant === "store" ? t("code_card.store_heading") : t("code_card.heading");
  const instruction = variant === "store" ? t("code_card.store_instruction") : t("code_card.instruction");

  const buildPng = () =>
    composeCardPng(
      pngQrRef.current?.querySelector("canvas") || qrWrapRef.current?.querySelector("canvas"), {
      name: distributor.name,
      code,
      heading,
      instruction,
      url: qrUrl,
      tagline: variant === "store" ? tagline : "",
    });

  const download = async () => {
    setBusy(true);
    try {
      const how = await saveCardPng(
        buildPng(),
        variant === "store" ? `${code}-store-qr.png` : `${code}-code-card.png`,
        qrUrl);
      if (how === "downloaded") toast.success(t("code_card.downloaded"));
      if (how === "shared") toast.success(t("code_card.share_saved"));
    } finally {
      setBusy(false);
    }
  };

  const print = () => {
    const url = buildPng();
    const w = window.open("", "_blank");
    if (!w) return toast.error(t("code_card.popup_blocked"));
    w.document.write(
      `<html><head><title>${code}</title></head><body style="margin:0;display:flex;justify-content:center;">` +
      `<img src="${url}" style="max-width:100%;max-height:100vh;" onload="window.print()"/></body></html>`
    );
    w.document.close();
  };

  const copy = async () => {
    await copyToClipboard(code, t("code_card.copied", { code }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid="code-card-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight">{t("code_card.title")}</h2>
        <p className="text-sm text-zinc-600 mt-1 mb-3">{t("code_card.subtitle")}</p>

        {/* v405.15 variant tabs */}
        <div className="flex rounded-lg border border-zinc-300 overflow-hidden mb-4" data-testid="code-card-variant-tabs">
          {[["store", t("code_card.store_tab")], ["balance", t("code_card.balance_tab")]].map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setVariant(v)}
              data-testid={`code-card-tab-${v}`}
              className={`flex-1 h-9 text-xs font-semibold ${variant === v ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Card preview */}
        <div className="border-4 border-[#c9a24a] rounded-lg p-5 text-center space-y-3" data-testid="code-card-preview">
          <p className="text-[11px] font-mono font-semibold tracking-[0.2em] text-[#8a6a1c] uppercase">
            {heading}
          </p>
          <p className="text-lg font-semibold text-zinc-950">{distributor.name}</p>
          <p className="inline-block text-3xl font-bold font-mono tracking-widest px-4 py-1.5 bg-[#fbf6e7] text-[#8a6a1c] border-2 border-[#c9a24a] rounded-lg">
            {code}
          </p>
          {variant === "store" && tagline.trim() && (
            <p className="text-sm italic font-semibold text-[#8a6a1c]" data-testid="code-card-tagline-preview">
              {tagline.trim()}
            </p>
          )}
          <div ref={qrWrapRef} className="flex justify-center pt-1">
            <QRCodeCanvas value={qrUrl} size={160} fgColor="#09090b" level="M" includeMargin />
          </div>
          {/* hidden hi-res QR used only when composing the shareable PNG */}
          <div ref={pngQrRef} className="hidden" aria-hidden="true">
            <QRCodeCanvas value={qrUrl} size={480} fgColor="#09090b" level="M" includeMargin />
          </div>
          {/* v412.4 — direct link ON the card: tappable + copyable */}
          <div className="flex items-stretch justify-center gap-1.5">
            <a
              href={qrUrl}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 flex items-center text-[11px] font-mono font-semibold text-[#8a6a1c] underline underline-offset-2 truncate"
              title={qrUrl}
              data-testid="code-card-store-link"
            >
              <span className="truncate">{qrUrl.replace(/^https?:\/\//, "")}</span>
            </a>
            <button
              type="button"
              onClick={() => copyToClipboard(qrUrl, t("code_card.link_copied"))}
              className="shrink-0 p-1 rounded text-[#8a6a1c] hover:bg-[#fbf6e7] transition-colors"
              title={t("code_card.link_copied")}
              data-testid="code-card-copy-link-btn"
            >
              <Copy size={12} weight="bold" />
            </button>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">{instruction}</p>
        </div>

        {/* v414.43 — editable tagline (store card only) */}
        {variant === "store" && (
          <div className="mt-3">
            <label className="block text-xs font-semibold mb-1 text-zinc-700">
              {t("code_card.tagline_label")}
            </label>
            <input
              type="text"
              value={tagline}
              maxLength={60}
              onChange={(e) => setTagline(e.target.value)}
              placeholder={t("code_card.tagline_placeholder")}
              className="w-full h-9 px-3 text-sm border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/50"
              data-testid="code-card-tagline-input"
            />
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <BrutalButton size="sm" className="flex-1" onClick={download} disabled={busy} data-testid="code-card-download-btn">
            <DownloadSimple size={14} weight="bold" /> {t("code_card.download")}
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" className="flex-1" onClick={print} data-testid="code-card-print-btn">
            <Printer size={14} weight="bold" /> {t("code_card.print")}
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" onClick={copy} data-testid="code-card-copy-btn" title={t("code_card.copy")}>
            <Copy size={14} weight="bold" />
          </BrutalButton>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-3 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
          data-testid="code-card-close-btn"
        >
          {t("common.close")}
        </button>
      </div>
    </div>
  );
}
